import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row } from 'reactstrap';
import { getInstallersForLicense } from '../../../core/api/endpoints/versions/versionsApi';
import { TachoReleaseInformationModel } from '../../../core/domainModels/versions/TachoInstallerModel';
import { searchInObject } from '../../../core/helpers/search';
import { ApplicationState } from '../../../state/ApplicationState';
import { deleteInstallerThunk, getAllInstallersThunk, resetDeleteInstallerError } from '../../../state/versions/reducer';
import { ClipLoader } from '../../components/ClipLoader';
import { TachoConfirmationModal } from '../../components/TachoConfirmationModal';
import { AddVersionModal } from '../../modals/versions/AddNewVersion';
import { UpdateVersionModal } from '../../modals/versions/UpdateVersionModal';
import { VersionsTable } from './components/VersionsTable';
import { VersionsToolbar } from './components/VersionsToolbar';
import { defaultAPIDateFormat } from '../../../core/helpers/moment';
import { DispatchType } from '../../../state/configureStore';

export const VersionsPage = () => {
    const fetchAllInstallersPending = useSelector((state: ApplicationState) => state.versions.fetchAllInstallersPending);
    const dispatch = useDispatch<DispatchType>();
    const { formatMessage } = useIntl();
    const selectAll = formatMessage({ id: 'versionsShowAll' });
    const installers = useSelector((state: ApplicationState) => state.versions.installers);
    const licences = useSelector((state: ApplicationState) => state.licences.licences);
    const deleteInstallerPending = useSelector((state: ApplicationState) => state.versions.deleteInstallerPending);
    const deleteInstallerError = useSelector((state: ApplicationState) => state.versions.deleteInstallerError);
    const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState(false);
    const [isOpenAddInstallerModal, setIsOpenAddInstallerModal] = useState(false);
    const [isOpenEditInstallerModal, setIsOpenEditInstallerModal] = useState(false);
    const [selectedInstallers, setSelectedInstallers] = useState<Array<TachoReleaseInformationModel>>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [installersForLicence, setInstallersForLicence] = useState<Array<TachoReleaseInformationModel>>(installers);
    const [getInstallersForLicencePending, setGetInstallersForLicencePending] = useState(false);
    const [getInstallersForLicenceError, setGetInstallersForLicenceError] = useState('');
    const installersToShow = useMemo(() => {
        return searchQuery
            ? installersForLicence.filter((installers: TachoReleaseInformationModel) => searchInObject(installers, searchQuery))
            : installersForLicence;
    }, [installersForLicence, searchQuery, installers]);
    const [selectedLicence, setSelectedLicence] = useState(selectAll);
    const [selectedVersion, setSelectedVersion] = useState('');

    useEffect(() => {
        if (selectedLicence === selectAll) {
            setInstallersForLicence(installers);
        } else {
            if (selectedLicence && selectedVersion) {
                const licence = licences.find(item => item.friendlyLicenseNumber === parseInt(selectedLicence));
                const licenceVersion = licence?.licenseVersions.find(
                    item =>
                        `${item.validFrom.toFormat(defaultAPIDateFormat)} - ${item.validTo.toFormat(defaultAPIDateFormat)}` ===
                        selectedVersion,
                )?.id;
                if (licence && licenceVersion) {
                    setGetInstallersForLicencePending(true);
                    getInstallersForLicense(licence?.licenseNumber, licenceVersion)
                        .then(result => {
                            setInstallersForLicence(result);
                        })
                        .catch(error => setGetInstallersForLicenceError(error))
                        .finally(() => setGetInstallersForLicencePending(false));
                }
            }
        }
    }, [selectedLicence, selectedVersion, licences, installers, selectAll]);
    return (
        <Container fluid className="flexColumnFullHeightContainer">
            <VersionsToolbar
                selectedVersions={selectedInstallers}
                selectedLicenceId={selectedLicence}
                selectedLicenceVersionId={selectedVersion}
                selectAllLicences={selectAll}
                getInstallersForLicenceError={getInstallersForLicenceError}
                openAddInstallerModal={() => setIsOpenAddInstallerModal(!isOpenAddInstallerModal)}
                openConfirmRemoveModal={() => {
                    dispatch(resetDeleteInstallerError());
                    setIsConfirmRemoveModalOpen(!isConfirmRemoveModalOpen);
                }}
                deletePending={deleteInstallerPending}
                openEditInstallerModal={() => setIsOpenEditInstallerModal(!isOpenEditInstallerModal)}
                onRefresh={() => dispatch(getAllInstallersThunk())}
                onSearchQueryChanged={setSearchQuery}
                setSelectedLicence={setSelectedLicence}
                setSelectedVersion={setSelectedVersion}
            />
            <Row className="ag-theme-balham tabTable flexGrowScrollableChild g-0">
                {fetchAllInstallersPending || getInstallersForLicencePending ? (
                    <ClipLoader />
                ) : (
                    <VersionsTable
                        onInstallerDoubleClicked={() => setIsOpenEditInstallerModal(!isOpenEditInstallerModal)}
                        onSelectionChanged={setSelectedInstallers}
                        installers={installersToShow}
                    />
                )}
            </Row>
            <TachoConfirmationModal
                isOpen={isConfirmRemoveModalOpen}
                onSubmit={() => {
                    dispatch(deleteInstallerThunk(selectedInstallers.map(installer => installer.id)))
                        .unwrap()
                        .then(() => setIsConfirmRemoveModalOpen(false))
                        .catch(() => undefined)
                        .finally(() => {
                            setSelectedInstallers([]);
                            dispatch(getAllInstallersThunk());
                        });
                }}
                notCloseOnSubmit
                onToggle={() => setIsConfirmRemoveModalOpen(!isConfirmRemoveModalOpen)}
                message={'versionsRemoveConfirmationMessage'}
                pending={deleteInstallerPending}
                errorToTranslate={deleteInstallerError}
                title={'usersDeleteVersion'}
            />
            <AddVersionModal
                isOpen={isOpenAddInstallerModal}
                onToggle={() => setIsOpenAddInstallerModal(!isOpenAddInstallerModal)}
            />
            <UpdateVersionModal
                installer={selectedInstallers[0]}
                isOpen={isOpenEditInstallerModal}
                onToggle={() => setIsOpenEditInstallerModal(!isOpenEditInstallerModal)}
            />
        </Container>
    );
};
